import {
  NormalizedInlineSegment,
  NormalizedTableCell,
} from "./markdownDiffTypes";
import { renderLeafTextDiff, wrapContentInSpan } from "./markdownTextDiff";

type SegmentPair = {
  previousIndex: number;
  currentIndex: number;
};

export const renderInlineValueDiff = (
  previousValue: string,
  currentValue: string,
  previousSegments?: NormalizedInlineSegment[],
  currentSegments?: NormalizedInlineSegment[]
): string => {
  const normalizedPreviousSegments = normalizeSegmentsForDiff(
    previousValue,
    previousSegments
  );
  const normalizedCurrentSegments = normalizeSegmentsForDiff(
    currentValue,
    currentSegments
  );

  if (
    normalizedPreviousSegments.length === 1 &&
    normalizedCurrentSegments.length === 1 &&
    normalizedPreviousSegments[0].kind === "text" &&
    normalizedCurrentSegments[0].kind === "text"
  ) {
    return renderLeafTextDiff(previousValue, currentValue, {
      wrapSpecialCharacters: true,
    });
  }

  return renderInlineSegmentsDiff(
    normalizedPreviousSegments,
    normalizedCurrentSegments
  );
};

export const renderTableCellDiff = (
  previousCell?: NormalizedTableCell,
  currentCell?: NormalizedTableCell
): string =>
  renderInlineValueDiff(
    previousCell?.rawMarkdown || "",
    currentCell?.rawMarkdown || "",
    previousCell?.inlineSegments,
    currentCell?.inlineSegments
  );

export const renderStructuredInlineContent = (
  segment: NormalizedInlineSegment,
  className?: "added" | "removed"
): string => {
  if (!className) {
    return segment.rawMarkdown;
  }

  if (segment.kind === "link") {
    return createLinkHtml(
      segment.url || "",
      segment.label || segment.url || segment.rawMarkdown,
      className,
      segment.title
    );
  }

  if (segment.kind === "image") {
    return createImageHtml(
      segment.url || "",
      segment.label || "",
      className,
      segment.title
    );
  }

  return wrapContentInSpan(segment.rawMarkdown, className === "added", className === "removed");
};

const renderInlineSegmentsDiff = (
  previousSegments: NormalizedInlineSegment[],
  currentSegments: NormalizedInlineSegment[]
): string => {
  const exactPairs = buildLcsPairs(
    previousSegments,
    currentSegments,
    (previousSegment, currentSegment) =>
      previousSegment.exactSignature === currentSegment.exactSignature
  );
  let pairIndex = 0;
  let previousIndex = 0;
  let currentIndex = 0;
  let result = "";

  while (
    previousIndex < previousSegments.length ||
    currentIndex < currentSegments.length
  ) {
    const nextPair = exactPairs[pairIndex];

    if (
      nextPair &&
      previousIndex === nextPair.previousIndex &&
      currentIndex === nextPair.currentIndex
    ) {
      result += renderStructuredInlineContent(
        currentSegments[currentIndex]
      );
      previousIndex += 1;
      currentIndex += 1;
      pairIndex += 1;
      continue;
    }

    const nextPreviousIndex = nextPair?.previousIndex ?? previousSegments.length;
    const nextCurrentIndex = nextPair?.currentIndex ?? currentSegments.length;
    result += renderInlineSegmentRange(
      previousSegments.slice(previousIndex, nextPreviousIndex),
      currentSegments.slice(currentIndex, nextCurrentIndex)
    );
    previousIndex = nextPreviousIndex;
    currentIndex = nextCurrentIndex;
  }

  return result;
};

const renderInlineSegmentRange = (
  previousRange: NormalizedInlineSegment[],
  currentRange: NormalizedInlineSegment[]
): string => {
  const segmentCount = Math.max(previousRange.length, currentRange.length);
  let result = "";

  for (let index = 0; index < segmentCount; index += 1) {
    const previousSegment = previousRange[index];
    const currentSegment = currentRange[index];

    if (previousSegment && currentSegment) {
      result += renderPairedSegmentDiff(previousSegment, currentSegment);
      continue;
    }

    if (previousSegment) {
      result += renderStructuredInlineContent(previousSegment, "removed");
      continue;
    }

    if (currentSegment) {
      result += renderStructuredInlineContent(currentSegment, "added");
    }
  }

  return result;
};

const renderPairedSegmentDiff = (
  previousSegment: NormalizedInlineSegment,
  currentSegment: NormalizedInlineSegment
): string => {
  if (
    previousSegment.kind === "text" &&
    currentSegment.kind === "text"
  ) {
    return renderLeafTextDiff(
      previousSegment.rawMarkdown,
      currentSegment.rawMarkdown,
      { wrapSpecialCharacters: true }
    );
  }

  if (
    previousSegment.kind === "break" &&
    currentSegment.kind === "break"
  ) {
    return currentSegment.rawMarkdown;
  }

  if (
    previousSegment.kind === currentSegment.kind &&
    previousSegment.rawMarkdown === currentSegment.rawMarkdown
  ) {
    return renderStructuredInlineContent(currentSegment);
  }

  return [
    renderStructuredInlineContent(previousSegment, "removed"),
    renderStructuredInlineContent(currentSegment, "added"),
  ].join("");
};

const normalizeSegmentsForDiff = (
  rawValue: string,
  inlineSegments?: NormalizedInlineSegment[]
): NormalizedInlineSegment[] => {
  if (inlineSegments && inlineSegments.length > 0) {
    return inlineSegments;
  }

  if (rawValue === "") {
    return [];
  }

  return [
    {
      kind: "text",
      rawMarkdown: rawValue,
      plainText: rawValue,
      normalizedText: rawValue.trim().toLowerCase(),
      exactSignature: `text:${rawValue.replace(/\s+/g, " ").trim()}`,
    },
  ];
};

const buildLcsPairs = (
  previousSegments: NormalizedInlineSegment[],
  currentSegments: NormalizedInlineSegment[],
  isMatch: (
    previousSegment: NormalizedInlineSegment,
    currentSegment: NormalizedInlineSegment
  ) => boolean
): SegmentPair[] => {
  const lcs = Array.from({ length: previousSegments.length + 1 }, () =>
    Array(currentSegments.length + 1).fill(0)
  );

  for (
    let previousIndex = previousSegments.length - 1;
    previousIndex >= 0;
    previousIndex -= 1
  ) {
    for (
      let currentIndex = currentSegments.length - 1;
      currentIndex >= 0;
      currentIndex -= 1
    ) {
      if (isMatch(previousSegments[previousIndex], currentSegments[currentIndex])) {
        lcs[previousIndex][currentIndex] =
          lcs[previousIndex + 1][currentIndex + 1] + 1;
      } else {
        lcs[previousIndex][currentIndex] = Math.max(
          lcs[previousIndex + 1][currentIndex],
          lcs[previousIndex][currentIndex + 1]
        );
      }
    }
  }

  const pairs: SegmentPair[] = [];
  let previousIndex = 0;
  let currentIndex = 0;

  while (
    previousIndex < previousSegments.length &&
    currentIndex < currentSegments.length
  ) {
    if (isMatch(previousSegments[previousIndex], currentSegments[currentIndex])) {
      pairs.push({ previousIndex, currentIndex });
      previousIndex += 1;
      currentIndex += 1;
      continue;
    }

    if (
      lcs[previousIndex + 1][currentIndex] >=
      lcs[previousIndex][currentIndex + 1]
    ) {
      previousIndex += 1;
    } else {
      currentIndex += 1;
    }
  }

  return pairs;
};

const createLinkHtml = (
  href: string,
  label: string,
  className: "added" | "removed",
  title?: string | null
): string => {
  const titleAttribute = title
    ? ` title="${escapeHtmlAttribute(title)}"`
    : "";

  return `<span class="${className}"><a href="${escapeHtmlAttribute(
    href
  )}"${titleAttribute}>${escapeHtmlText(label)}</a></span>`;
};

const createImageHtml = (
  src: string,
  alt: string,
  className: "added" | "removed",
  title?: string | null
): string => {
  const titleAttribute = title
    ? ` title="${escapeHtmlAttribute(title)}"`
    : "";
  const altAttribute = ` alt="${escapeHtmlAttribute(alt)}"`;

  return `<span class="${className}"><img src="${escapeHtmlAttribute(
    src
  )}"${altAttribute}${titleAttribute} /></span>`;
};

const escapeHtmlAttribute = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeHtmlText = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
